import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { useModelService } from 'services/model/model.service';
import { DataResult } from 'models/data';
import { useAppStore } from 'hooks/state/useAppStore';
import { Interpretation } from 'components/MeasureComparison';
import { MarketplacesInfoContent } from './components/MarketplacesInfoContent';
import {
  PerformanceGapDisplay,
  PerformanceGapEmptyState,
} from 'pages/Benchmarking/common/performance-gap/PerformanceGapDisplay';
import { getCombinedBenchmarkEmptyStateType } from 'pages/Benchmarking/common/performance-gap/getBenchmarkEmptyStateType';
import { useBenchmarkDataResult } from 'pages/Benchmarking/queryHooks';
import { getErrorMessage } from 'utils/getErrorMessage';

interface MarketplacesDashboardInfo {
  data: DataResult;
  benchmark: string;
}

export function MarketplacesDashboard() {
  const { getMeasures, getAttribute } = useModelService();

  const chnMarketplace = getAttribute('chnMarketplace');

  const [dashboardData, setDashboardData] = useState<MarketplacesDashboardInfo>();

  const { marketplaces } = useAppStore();

  const { data: inflightDashboardData, isPending, isError, error } = useBenchmarkDataResult('marketplaces');
  const { benchmark } = useAppStore();

  const { setContent } = useInfoDrawer();

  useEffect(() => {
    const marketplaceDashboardInfo = {
      titleKey: 'Marketplaces Benchmarking',
      subtitleKey: 'info.content.all_dates_utc',
      content: <MarketplacesInfoContent />,
    };

    setContent(marketplaceDashboardInfo);

    return () => {
      setContent(null);
    };
  }, [setContent]);

  useEffect(() => {
    if (inflightDashboardData) {
      const filteredMarketplaces = inflightDashboardData.sets.marketplaces.filter(marketplace =>
        marketplaces && marketplaces.length > 0 && marketplace.attributes.chnMarketplace
          ? marketplaces.includes(marketplace.attributes.chnMarketplace)
          : true
      );
      setDashboardData({
        ...inflightDashboardData,
        benchmark: benchmark,
        data: {
          ...inflightDashboardData,
          sets: {
            ...inflightDashboardData?.sets,
            marketplaces: filteredMarketplaces,
          },
        },
      });
    }
  }, [inflightDashboardData]);

  if (!dashboardData && !isError) {
    return <MetricsLoadingSpinner />;
  }

  if (isError) {
    const errorMessage = getErrorMessage(error);
    return (
      <PageContainer>
        <Tile
          titleId="benchmarking.marketplaces"
          errorDetails={errorMessage}
          testId="benchmarking-marketplaces-dashboard-error"
        />
      </PageContainer>
    );
  }

  if (!dashboardData) {
    return <MetricsLoadingSpinner />;
  }

  const [ordGmvVal, retQtyRateOD, cncQtyRate] = getMeasures(['ordGmvVal', 'retQtyRateExpOD', 'cncQtyRate']);

  const dataEntries = dashboardData.data.sets.marketplaces;
  const combinedReturnsAndCancellationsEmptyState = getCombinedBenchmarkEmptyStateType(
    dataEntries,
    [retQtyRateOD, cncQtyRate],
    dashboardData.benchmark
  );

  return (
    <PageContainer>
      <PageSectionTitle>
        <FormattedMessage id="benchmarking.titles.additional.performance" />
      </PageSectionTitle>
      <PerformanceGapDisplay
        dataSet={dataEntries}
        improvementMeasure={ordGmvVal}
        groupingAttribute={chnMarketplace}
        interpretation={Interpretation.POSITIVE}
        drilldown
        benchmark={dashboardData.benchmark}
        isPending={isPending}
        opportunitiesTestId="bm-marketplaces-growth-opportunities"
        chartTileTestId="bm-marketplaces-growth-chart"
        chartTestId="bm-marketplaces-ordered-gmv-growth"
      />
      <PageSectionTitle>
        <FormattedMessage id="benchmarking.titles.returns-canellation" />
      </PageSectionTitle>
      {combinedReturnsAndCancellationsEmptyState ? (
        <PerformanceGapEmptyState
          type={combinedReturnsAndCancellationsEmptyState}
          testId="bm-marketplaces-returns-cancellations-empty"
        />
      ) : (
        <>
          <PerformanceGapDisplay
            dataSet={dataEntries}
            improvementMeasure={retQtyRateOD}
            groupingAttribute={chnMarketplace}
            interpretation={Interpretation.NEGATIVE}
            drilldown
            benchmark={dashboardData.benchmark}
            isPending={isPending}
            opportunitiesTestId="bm-marketplaces-returns-opportunities"
            chartTileTestId="bm-marketplaces-returns-chart"
            chartTestId="bm-marketplaces-returns-chart-plot"
          />
          <PerformanceGapDisplay
            dataSet={dataEntries}
            improvementMeasure={cncQtyRate}
            groupingAttribute={chnMarketplace}
            interpretation={Interpretation.NEGATIVE}
            drilldown
            benchmark={dashboardData.benchmark}
            isPending={isPending}
            opportunitiesTestId="bm-marketplaces-cancellations-opportunities"
            chartTileTestId="bm-marketplaces-cancellations-chart"
            chartTestId="bm-marketplaces-cancellations-chart-plot"
          />
        </>
      )}
    </PageContainer>
  );
}
